import axiosInstance from "../Axios/axios";
import { supportConstants } from "../Constants/supportConstant";

// ─────────────────────────────────────────────────────────────────────────
// Customer Service (CRM + ticketing) actions.
//
// These are written against the endpoints the API is expected to expose; none
// of /support/* exists on api.upnmg.com yet, so this file doubles as the
// backend contract. Read actions swallow errors into an empty payload so the
// pages render their empty state instead of crashing before the API lands.
// Write actions surface a toast-able response, matching inventoryAction.
// ─────────────────────────────────────────────────────────────────────────

const setLoading = (dispatch, isLoading) =>
  dispatch({ type: supportConstants.LOADING, isLoading });

const setResponse = (dispatch, state, message) =>
  dispatch({ type: supportConstants.RESPONSE, response: { state, message } });

const errMessage = (e) =>
  e?.response?.data?.message ?? "Connection failed. Try again later";

// Read endpoints are not yet implemented server-side; a 404 must not blank the
// UI with an error toast on every page load, so reads fall back to `fallback`.
const safeGet = async (url, fallback) => {
  try {
    const res = await axiosInstance.get(url);
    return res?.data ?? fallback;
  } catch (e) {
    return fallback;
  }
};

// ── Tickets ────────────────────────────────────────────────────────────
// GET /support/tickets?status=&queue=&priority=&assigned_to=&search=&breached=
export const GetTickets = (query = {}) => {
  return async (dispatch) => {
    setLoading(dispatch, true);
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && v !== "ALL") {
        params.append(k, v);
      }
    });
    const data = await safeGet(`/support/tickets?${params.toString()}`, []);
    dispatch({
      type: supportConstants.GET_TICKETS,
      payload: { tickets: data?.docs ?? data ?? [] },
    });
    setLoading(dispatch, false);
  };
};

// GET /support/tickets/{ticket_id}
export const GetTicketById = (ticket_id) => {
  return async (dispatch) => {
    setLoading(dispatch, true);
    const data = await safeGet(`/support/tickets/${ticket_id}`, {});
    dispatch({
      type: supportConstants.GET_TICKET_DETAILED,
      payload: { ticket: data },
    });
    setLoading(dispatch, false);
  };
};

// GET /support/tickets/{ticket_id}/thread  → ordered messages + audit events
export const GetTicketThread = (ticket_id) => {
  return async (dispatch) => {
    dispatch({ type: supportConstants.THREAD_LOADING, isLoading: true });
    const data = await safeGet(`/support/tickets/${ticket_id}/thread`, []);
    dispatch({
      type: supportConstants.GET_TICKET_THREAD,
      payload: { thread: data?.docs ?? data ?? [] },
    });
    dispatch({ type: supportConstants.THREAD_LOADING, isLoading: false });
  };
};

// POST /support/tickets
export const CreateTicket = (data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.post(`/support/tickets`, data);
      setResponse(dispatch, "SUCCESS", "Ticket created");
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// PUT /support/tickets/{ticket_id}  { subject?, description?, category?, phone? }
// Edits the reported issue. Status/priority/assignment have their own actions.
export const EditTicket = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.put(`/support/tickets/${ticket_id}`, data);
      setResponse(dispatch, "SUCCESS", "Ticket updated");
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// POST /support/tickets/{ticket_id}/reply  { body, is_internal_note }
export const ReplyToTicket = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.post(`/support/tickets/${ticket_id}/reply`, data);
      setResponse(
        dispatch,
        "SUCCESS",
        data?.is_internal_note ? "Internal note added" : "Reply sent"
      );
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// PUT /support/tickets/{ticket_id}/status  { status, reason }
export const UpdateTicketStatus = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.put(`/support/tickets/${ticket_id}/status`, data);
      setResponse(dispatch, "SUCCESS", `Ticket marked ${data?.status}`);
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// PUT /support/tickets/{ticket_id}/assign  { assigned_to }
export const AssignTicket = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.put(`/support/tickets/${ticket_id}/assign`, data);
      setResponse(dispatch, "SUCCESS", "Ticket assigned");
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// PUT /support/tickets/{ticket_id}/escalate  { queue, reason }
// Hands the ticket to a department head (loans / mart / IT) — head-only action.
export const EscalateTicket = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.put(`/support/tickets/${ticket_id}/escalate`, data);
      setResponse(dispatch, "SUCCESS", `Escalated to ${data?.queue}`);
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// PUT /support/tickets/{ticket_id}/priority  { priority }
export const UpdateTicketPriority = (ticket_id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      await axiosInstance.put(`/support/tickets/${ticket_id}/priority`, data);
      setResponse(dispatch, "SUCCESS", `Priority set to ${data?.priority}`);
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    } finally {
      setLoading(dispatch, false);
    }
  };
};

// ── Dashboard / reporting ──────────────────────────────────────────────
// GET /support/tickets/stats → { open, pending, escalated, resolved_today,
//                                unassigned, breached, by_queue[], by_priority[] }
export const GetTicketStats = () => {
  return async (dispatch) => {
    setLoading(dispatch, true);
    const data = await safeGet(`/support/tickets/stats`, {});
    dispatch({ type: supportConstants.GET_STATS, payload: { stats: data } });
    setLoading(dispatch, false);
  };
};

// GET /support/reports?from=&to= → volume, resolution time, per-agent rows
export const GetSupportReports = (query = {}) => {
  return async (dispatch) => {
    setLoading(dispatch, true);
    const params = new URLSearchParams();
    if (query.from) params.append("from", query.from);
    if (query.to) params.append("to", query.to);
    const data = await safeGet(`/support/reports?${params.toString()}`, {});
    dispatch({ type: supportConstants.GET_REPORTS, payload: { reports: data } });
    setLoading(dispatch, false);
  };
};

// GET /support/agents → system users who can own a ticket
export const GetSupportAgents = () => {
  return async (dispatch) => {
    const data = await safeGet(`/support/agents`, []);
    dispatch({
      type: supportConstants.GET_AGENTS,
      payload: { agents: data?.docs ?? data ?? [] },
    });
  };
};

// ── Member 360 ─────────────────────────────────────────────────────────
// GET /support/members/{staff_id}/360
// Consolidates data that already exists behind /users/members/{staff_id},
// /loans/member-history/{staff_id}, funds, dues and mart orders into one call.
// Falls back to composing it client-side from the endpoints that DO exist, so
// the panel is useful before /360 ships.
export const GetMember360 = (staff_id) => {
  return async (dispatch) => {
    setLoading(dispatch, true);

    const consolidated = await safeGet(
      `/support/members/${staff_id}/360`,
      null
    );

    if (consolidated) {
      dispatch({
        type: supportConstants.GET_MEMBER_360,
        payload: { member360: consolidated },
      });
      setLoading(dispatch, false);
      return;
    }

    // Fallback: stitch the profile together from the existing per-module
    // endpoints. Each piece degrades independently.
    const [profile, loans] = await Promise.all([
      safeGet(`/users/members/${staff_id}`, {}),
      safeGet(`/loans/member-history/${staff_id}`, []),
    ]);

    const loanRows = loans?.docs ?? loans ?? [];

    dispatch({
      type: supportConstants.GET_MEMBER_360,
      payload: {
        member360: {
          profile: profile?.docs ?? profile ?? {},
          loans: loanRows,
          orders: [],
          funds: [],
          dues: [],
          // The consolidated endpoint returns these; compute what we can here
          // so the profile header renders the same way on both paths. Funds
          // are unreachable in this mode, hence null rather than 0 — the panel
          // shows "—" instead of claiming the member contributed nothing.
          totals: {
            funds_total: null,
            dues_total: null,
            loans_total: loanRows.reduce(
              (acc, l) => acc + (Number(l?.initialAmount ?? l?.amount) || 0),
              0
            ),
            loan_balance: null,
          },
          partial: true, // panel shows a "limited view" hint when true
        },
      },
    });
    setLoading(dispatch, false);
  };
};

// GET /support/members/{staff_id}/tickets
export const GetMemberTickets = (staff_id) => {
  return async (dispatch) => {
    const data = await safeGet(`/support/members/${staff_id}/tickets`, []);
    dispatch({
      type: supportConstants.GET_MEMBER_TICKETS,
      payload: { memberTickets: data?.docs ?? data ?? [] },
    });
  };
};

export const ClearTicketDetailed = () => {
  return async (dispatch) => {
    dispatch({ type: supportConstants.CLEAR_TICKET_DETAILED });
  };
};

export const ResetSupportResponse = () => {
  return async (dispatch) => {
    dispatch({
      type: supportConstants.RESPONSE,
      response: { state: null, message: "" },
    });
  };
};
